import React, { CSSProperties, ReactElement, useEffect, useState } from 'react';
import { ButtonGroup, Button } from 'react-bootstrap';
import { BiChevronLeft, BiChevronRight } from 'react-icons/bi';
import { useNavigate, useLocation } from 'react-router-dom';
import { State } from 'history';
import './BoutonPreSuiv.scss';

interface TableauState {
    pos: number;
    tableauEntite: string[];
}

/**
 * Lorsqu'on arrive sur un formulaire d'entité à partir de la liste du dashboard,
 * afficher deux boutons de navigation pour parcourir la liste. Le bouton '<' va à
 * l'entité précédente. Le bouton '>' va au suivant.
 *
 * @returns deux boutons de navigation
 */
function BoutonPreSuiv(): ReactElement {
    const navigate = useNavigate();
    const location = useLocation();
    const [avoirLocationState, setAvoirLocationState] = useState<boolean>(false);

    useEffect(() => {
        verifierLocationState();
    }, [location.state]);

    /**
     * Vérifier s'il y a les variables de position et de tableauEntite dans le
     * location state. Sans ça on afficherait pas les boutons de navigation
     *
     * @returns void
     */
    function verifierLocationState(): void {
        if (location.state === null) return;
        const avoirTableauEntite: boolean = location.state.hasOwnProperty('tableauEntite');
        const avoirPos: boolean = location.state.hasOwnProperty('pos');
        setAvoirLocationState(avoirTableauEntite && avoirPos);
    }

    /**
     * Callback lorsqu'on clique sur '<'. Navigue à l'entité précédente
     * @returns void
     */
    function gererPrecedent(): void {
        if (!avoirLocationState) return;
        const { pos, tableauEntite } = location.state as TableauState;

        const link = tableauEntite[pos - 1];
        const state: State = {
            pos: pos - 1,
            tableauEntite: tableauEntite,
        };
        navigate(`${link}`, { state: state });
    }

    /**
     * Callback lorsqu'on clique sur '>'. Navigue à l'entité suivante
     * @returns void
     */
    function gererSuivant(): void {
        if (!avoirLocationState) return;
        const { pos, tableauEntite } = location.state as TableauState;

        const link = tableauEntite[pos + 1];
        const state: State = {
            pos: pos + 1,
            tableauEntite: tableauEntite,
        };
        navigate(`${link}`, { state: state });
    }

    /**
     * Bloque ou non le bouton '<' dépendemment s'il y a un élément
     * précédent dans la liste
     * @returns true s'il y a un élément précédent dans la liste
     */
    function avoirPrecedent(): boolean {
        if (!avoirLocationState) return false;
        const { pos } = location.state as TableauState;

        return pos > 0;
    }

    /**
     * Bloque ou non le bouton '>' dépendemment s'il y a un élément
     * suivant dans la liste
     * @returns true s'il y a un élément suivant dans la liste
     */
    function avoirSuivant(): boolean {
        if (!avoirLocationState) return false;
        const { pos, tableauEntite } = location.state as TableauState;

        return pos + 1 < tableauEntite.length;
    }

    /**
     * Affiche la position de l'entité par rapport à la liste du dashboard
     * @returns la position en texte
     */
    function AfficherEtat(): ReactElement {
        const { pos, tableauEntite } = location.state as TableauState;
        return (
            <small className="my-auto">
                {pos + 1} de {tableauEntite.length}
            </small>
        );
    }

    const classBouton: string = 'text-dark px-2 border-0 rounded-circle';

    if (!avoirLocationState) return <></>;

    return (
        <ButtonGroup className="flex-nowrap mr-5 mt-auto mb-auto chevron-group">
            <Button
                className={classBouton}
                variant="outline-light"
                onClick={gererPrecedent}
                disabled={!avoirPrecedent()}
            >
                <BiChevronLeft className="chevron" />
            </Button>
            <AfficherEtat />
            <Button className={classBouton} variant="outline-light" onClick={gererSuivant} disabled={!avoirSuivant()}>
                <BiChevronRight className="chevron" />
            </Button>
        </ButtonGroup>
    );
}

export default BoutonPreSuiv;
